import { useMemo } from 'react';
import DashboardCard from '@/pages/dashboard/DashboardCard';
import DashboardAreaChart from '@/pages/dashboard/DashboardAreaChart';
import HorizontalBarIndicator from '@/pages/dashboard/HorizontalBarIndicator';
import { useDashboardContentPerformance } from '@/lib/api/hooks/dashboard';
import { Loader } from '@/components/ui';
import Subscribers from '@/pages/dashboard/Subscribers';

const formatPercent = (value: number): string => `${Math.abs(value).toFixed(0)}%`;

const formatCompact = (value: number): string => {
	if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
	if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
	if (value >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
	return String(value);
};

const ContentPerformance = () => {
	const { data, isFetching, isError, refetch } = useDashboardContentPerformance();

	const chartData = useMemo(() => {
		if (!data?.last7DaysChart) return [];
		return data.last7DaysChart.map((item) => ({
			day: new Date(item.viewDate).toLocaleDateString('en-US', { weekday: 'short' }),
			views: item.views,
		}));
	}, [data?.last7DaysChart]);

	const topContent = data?.top3ContentPages || [];
	const maxTopContentViews = Math.max(...topContent.map((item) => item.views), 0);
	const sparklineGradientId = 'content-performance-sparkline-gradient';

	if (isError) {
		return (
			<DashboardCard title="Content Performance">
				<div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
					<p className="text-sm text-gray-600">Error loading content performance.</p>
					<button onClick={() => refetch()} className="rounded-lg bg-[#681628] px-4 py-2 text-sm font-medium text-white">
						Retry
					</button>
				</div>
			</DashboardCard>
		);
	}

	if (isFetching || !data) {
		return (
			<DashboardCard title="Content Performance">
				<div className="flex min-h-[260px] items-center justify-center">
					<Loader size="md" variant="wine" />
				</div>
			</DashboardCard>
		);
	}

	const isPositiveChange = data.percentageChange > 0;
	const isNegativeChange = data.percentageChange < 0;
	const trendClass = isPositiveChange ? 'text-emerald-600' : isNegativeChange ? 'text-red-500' : 'text-gray-500';
	const trendStroke = isNegativeChange ? '#ef4444' : '#16a34a';

	return (
		<DashboardCard title="Content Performance (Last 30 Days)">
			<div className="space-y-6">

        <div className='grid gap-4 2xl:grid-cols-2'>
          	<div className="rounded-lg flex flex-col border border-gray p-4 gap-2 h-full">
  <p className="text-sm font-medium text-gray-600">
    Page Views
  </p>

  <div className="flex flex-1 items-stretch gap-4 min-h-0">
    
    {/* Left stats */}
    <div className="shrink-0">
      <p className="text-3xl font-semibold text-gray-900">
        {formatCompact(data.totalPageViews)}
      </p>
      <span className={`inline-flex items-center gap-0.5 font-medium ${trendClass}`}>
        {isPositiveChange ? '↑' : isNegativeChange ? '↓' : ''}
        {formatPercent(data.percentageChange)}
      </span>
    </div>

    {/* Chart wrapper (IMPORTANT) */}
    <div className="flex-1 min-h-0">
      <DashboardAreaChart
        data={chartData}
        yKey="views"
        stroke={trendStroke}
        gradientId={sparklineGradientId}
      />
    </div>

  </div>
</div>

<Subscribers />

        </div>
		

		

				<div>
					<h3 className="mb-3 text-sm font-semibold text-gray-800">Top Content</h3>
					<div className="space-y-3">
						{topContent.length > 0 ? (
							topContent.map((item) => (
								<HorizontalBarIndicator
									key={item.name}
									label={item.name}
									value={item.views}
									maxValue={maxTopContentViews}
								/>
							))
						) : (
							<p className="text-sm text-gray-500">No top content data available.</p>
						)}
					</div>
				</div>
			</div>
		</DashboardCard>
	);
};

export default ContentPerformance;
